import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, num, API } from "../lib/api";
import { GlassCard } from "../components/GlassCard";
import { Spinner } from "../components/Spinner";
import { Plus, Search, Power, FileText, LayoutGrid, List as ListIcon, ArrowUp, ArrowDown, Pencil, Check, X, Archive } from "lucide-react";
import CreateAccountDialog from "../components/CreateAccountDialog";

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [level, setLevel] = useState("owner");
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("accounts_view") || "list");
  const [sortBy, setSortBy] = useState("name"); // name | balance
  const [sortDir, setSortDir] = useState("asc"); // asc | desc
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);

  const downloadAllLedgersZip = async () => {
    setZipBusy(true);
    try {
      const resp = await api.get("/backup/all-ledgers.zip", { responseType: "blob" });
      const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"] || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const filename = m ? decodeURIComponent(m[1]) : `solartoner-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      alert(`Backup download failed: ${err?.response?.data?.detail || err?.response?.statusText || err.message}`);
    } finally {
      setZipBusy(false);
    }
  };
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data.accounts || []);
    setLevel(data.workspace_level || "owner");
  }, []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("workspace-changed", h);
    return () => window.removeEventListener("workspace-changed", h);
  }, [refresh]);

  const toggleActive = async (e, a) => {
    e.stopPropagation();
    try {
      await api.patch(`/accounts/${a.id}`, { active: !(a.active ?? true) });
      refresh();
    } catch (err) {
      // noop
    }
  };

  const startRename = (e, a) => {
    e.stopPropagation();
    setEditingId(a.id);
    setEditName(a.name);
    setEditError("");
  };

  const cancelRename = (e) => {
    if (e) e.stopPropagation();
    setEditingId(null);
    setEditName("");
    setEditError("");
  };

  const commitRename = async (e, a) => {
    if (e) e.stopPropagation();
    const trimmed = editName.trim();
    if (!trimmed) { setEditError("Name required"); return; }
    if (trimmed === a.name) { cancelRename(); return; }
    setEditSaving(true);
    setEditError("");
    try {
      await api.patch(`/accounts/${a.id}`, { name: trimmed });
      setEditingId(null);
      setEditName("");
      await refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Rename failed";
      setEditError(String(msg));
    } finally {
      setEditSaving(false);
    }
  };

  const toggleSelected = (e, id) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkSetActive = async (active) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const verb = active ? "reactivate" : "mark inactive";
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${ids.length} account${ids.length > 1 ? "s" : ""}?`)) return;
    setBulkBusy(true);
    try {
      await api.post("/accounts/bulk-active", { ids, active });
      clearSelection();
      await refresh();
    } catch (err) {
      alert(`Bulk update failed: ${err?.response?.data?.detail || err?.message || "unknown error"}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadUpload = async (e, uploadId) => {
    e.stopPropagation();
    if (!uploadId) {
      alert("This account has no saved source file.");
      return;
    }
    try {
      // Use the shared axios client so Authorization + X-Workspace + X-Mode
      // headers are all sent identically to the rest of the app. Raw fetch()
      // was missing X-Mode, which on some deploys caused the request to be
      // rejected before any HTTP response could be read (surfaced as
      // "Failed to fetch" in the browser).
      const resp = await api.get(`/backfill/uploads/${uploadId}/download`, {
        responseType: "blob",
      });
      // Try to pull the real filename from Content-Disposition
      const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"] || "";
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      let filename = m ? decodeURIComponent(m[1]) : "";
      if (!filename) {
        const ctype = ((resp.headers["content-type"] || resp.headers["Content-Type"]) || "").toLowerCase();
        const ext = ctype.includes("zip") ? "zip" : "pdf";
        filename = `source-${uploadId.slice(-6)}.${ext}`;
      }
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // Axios errors carry HTTP status + backend detail. Prefer those over the
      // generic transport-layer message when they exist.
      const status = err?.response?.status;
      let detail = "";
      const raw = err?.response?.data;
      if (raw) {
        // Blob → try to read as text for JSON detail
        if (raw instanceof Blob) {
          try {
            const text = await raw.text();
            try {
              const j = JSON.parse(text);
              detail = j.detail || text;
            } catch { detail = text; }
          } catch { /* ignore */ }
        } else if (typeof raw === "object" && raw.detail) {
          detail = raw.detail;
        } else {
          detail = String(raw);
        }
      }
      const msg = detail || (status ? `HTTP ${status}` : err?.message) || "Unknown error";
      alert(`Download failed: ${msg}`);
    }
  };

  const setView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("accounts_view", mode);
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    let list = accounts.filter((a) => {
      if (!showInactive && a.active === false) return false;
      if (q && !a.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortBy === "name") cmp = a.name.localeCompare(b.name);
      else if (sortBy === "balance") cmp = (a.balance || 0) - (b.balance || 0);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [accounts, showInactive, q, sortBy, sortDir]);

  const sortIconEl = (col) =>
    sortBy !== col ? null : sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />;

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-stone-500">All accounts · {filtered.length}</p>
          <h1 className="font-serif text-5xl">Accounts</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="show-inactive-toggle"
              className="rounded"
            />
            Show inactive
          </label>
          <div className="flex rounded-full bg-white/70 border border-stone-200 p-0.5">
            <button
              onClick={() => setView("list")}
              className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 transition-colors ${viewMode === "list" ? "bg-[color:var(--gold)] text-white" : "text-stone-600"}`}
              data-testid="view-list-btn"
              title="Compact list"
            >
              <ListIcon size={12} /> List
            </button>
            <button
              onClick={() => setView("cards")}
              className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 transition-colors ${viewMode === "cards" ? "bg-[color:var(--gold)] text-white" : "text-stone-600"}`}
              data-testid="view-cards-btn"
              title="Card view"
            >
              <LayoutGrid size={12} /> Cards
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="accounts-search"
              className="pl-9 pr-4 py-2 rounded-full bg-white/80 border border-white/90 text-sm outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
            />
          </div>
          {level === "owner" && (
            <button
              onClick={downloadAllLedgersZip}
              disabled={zipBusy}
              className="btn-ghost flex items-center gap-2 text-xs disabled:opacity-60"
              data-testid="accounts-backup-zip-btn"
              title="Download every account's ledger + summary + balance sheet as one ZIP"
            >
              <Archive size={14} />
              {zipBusy ? "Zipping…" : "Backup all as ZIP"}
            </button>
          )}
          {level === "owner" && (
            <button
              onClick={() => setOpen(true)}
              className="btn-gold flex items-center gap-2"
              data-testid="accounts-new-btn"
            >
              <Plus size={16} /> New account
            </button>
          )}
        </div>
      </div>

      {level === "owner" && selectedIds.size > 0 && (
        <div className="glass rounded-full px-4 py-2 flex items-center gap-3 sticky top-2 z-20 bg-white/90 border border-[color:var(--gold)]/40 shadow-sm" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <span className="text-stone-300">·</span>
          <button
            onClick={() => bulkSetActive(false)}
            disabled={bulkBusy}
            className="text-xs px-3 py-1 rounded-full bg-stone-800 text-white hover:bg-stone-900 disabled:opacity-50 flex items-center gap-1"
            data-testid="bulk-deactivate-btn"
          >
            {bulkBusy ? <Spinner size={12} /> : <Power size={12} />} {bulkBusy ? "Working…" : "Mark inactive"}
          </button>
          <button
            onClick={() => bulkSetActive(true)}
            disabled={bulkBusy}
            className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 flex items-center gap-1"
            data-testid="bulk-reactivate-btn"
          >
            {bulkBusy ? <Spinner size={12} /> : <Power size={12} />} Reactivate
          </button>
          <button
            onClick={clearSelection}
            disabled={bulkBusy}
            className="ml-auto text-xs px-3 py-1 rounded-full border border-stone-200 hover:bg-white/80 disabled:opacity-50"
            data-testid="bulk-clear-btn"
          >
            Clear
          </button>
        </div>
      )}

      {viewMode === "list" ? (
        <GlassCard className="p-0 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-5 py-2.5 text-[11px] uppercase tracking-wider text-stone-500 bg-white/50 border-b border-stone-200/60 sticky top-0">
            <div className="col-span-5 flex items-center gap-3">
              {level === "owner" && filtered.length > 0 && (
                <input
                  type="checkbox"
                  className="rounded cursor-pointer"
                  title="Select all visible"
                  checked={filtered.length > 0 && filtered.every((a) => selectedIds.has(a.id))}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedIds(new Set(filtered.map((a) => a.id)));
                    else clearSelection();
                  }}
                  data-testid="bulk-select-all"
                />
              )}
              <button onClick={() => toggleSort("name")} className="text-left flex items-center gap-1 hover:text-stone-700">
                Account {sortIconEl("name")}
              </button>
            </div>
            <button onClick={() => toggleSort("balance")} className="col-span-3 text-right flex items-center gap-1 justify-end hover:text-stone-700">
              Balance {sortIconEl("balance")}
            </button>
            <div className="col-span-2 text-right">Opening</div>
            <div className="col-span-1 text-center">Source</div>
            <div className="col-span-1 text-center">Status</div>
          </div>
          <div className="divide-y divide-stone-200/40 max-h-[70vh] overflow-y-auto">
            {filtered.map((a) => {
              const inactive = a.active === false;
              const isEditing = editingId === a.id;
              const isSelected = selectedIds.has(a.id);
              return (
                <div
                  key={a.id}
                  onClick={() => { if (!isEditing) navigate(`/entries?account=${a.id}`); }}
                  className={`grid grid-cols-12 gap-3 px-5 py-2.5 items-center transition-colors ${isEditing ? "bg-amber-50/70" : isSelected ? "bg-[color:var(--gold-soft)]/60 hover:bg-[color:var(--gold-soft)]/80 cursor-pointer" : "cursor-pointer hover:bg-white/70"} ${inactive ? "opacity-90" : ""}`}
                  data-testid={`accounts-row-${a.id}`}
                >
                  <div className="col-span-5 font-medium truncate flex items-center gap-2">
                    {level === "owner" && !isEditing && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => toggleSelected(e, a.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded cursor-pointer flex-shrink-0"
                        data-testid={`bulk-select-${a.id}`}
                      />
                    )}
                    {isEditing ? (
                      <>
                        <input
                          autoFocus
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") commitRename(e, a);
                            else if (e.key === "Escape") cancelRename(e);
                          }}
                          disabled={editSaving}
                          className="flex-1 px-2.5 py-1 rounded-lg bg-white border border-[color:var(--gold)]/50 text-sm outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
                          data-testid={`account-rename-input-${a.id}`}
                        />
                        <button
                          onClick={(e) => commitRename(e, a)}
                          disabled={editSaving}
                          className="p-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                          title="Save"
                          data-testid={`account-rename-save-${a.id}`}
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={cancelRename}
                          disabled={editSaving}
                          className="p-1 rounded-full bg-stone-200 text-stone-700 hover:bg-stone-300 disabled:opacity-50"
                          title="Cancel"
                          data-testid={`account-rename-cancel-${a.id}`}
                        >
                          <X size={12} />
                        </button>
                        {editError && <span className="text-[10px] text-rose-600" data-testid={`account-rename-err-${a.id}`}>{editError}</span>}
                      </>
                    ) : (
                      <>
                        <span className={`truncate ${inactive ? "text-stone-400 dark:text-stone-500 line-through decoration-stone-500/40 decoration-1" : ""}`}>{a.name}</span>
                        {inactive && (
                          <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 flex-shrink-0" data-testid={`accounts-row-inactive-${a.id}`}>
                            Off
                          </span>
                        )}
                        {level === "owner" && (
                          <button
                            onClick={(e) => startRename(e, a)}
                            className="p-1 rounded-full text-stone-400 hover:text-stone-700 hover:bg-white/80 opacity-0 group-hover:opacity-100 [.grid:hover_&]:opacity-100"
                            title="Rename account"
                            data-testid={`account-rename-btn-${a.id}`}
                          >
                            <Pencil size={11} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <div
                    className={`col-span-3 text-right font-serif text-lg ${
                      a.balance > 0 ? "text-emerald-700" : a.balance < 0 ? "text-rose-700" : "text-stone-500"
                    }`}
                  >
                    {num(a.balance)}
                  </div>
                  <div className="col-span-2 text-right text-xs text-stone-500">{num(a.opening_balance)}</div>
                  <div className="col-span-1 flex justify-center">
                    {a.source_upload_ids && a.source_upload_ids.length > 0 ? (
                      <button
                        onClick={(e) => downloadUpload(e, a.source_upload_ids[0])}
                        className="p-1 rounded-full text-stone-500 hover:text-[color:var(--gold-deep)] hover:bg-white/80"
                        title={`Download source file (${a.source_upload_ids.length})`}
                        data-testid={`source-pdf-${a.id}`}
                      >
                        <FileText size={13} />
                      </button>
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </div>
                  <div className="col-span-1 flex justify-center">
                    {level === "owner" ? (
                      <button
                        onClick={(e) => toggleActive(e, a)}
                        className={`p-1 rounded-full transition-colors ${inactive ? "bg-rose-100 text-rose-700 hover:bg-rose-200" : "text-stone-400 hover:text-stone-700 hover:bg-white/80"}`}
                        title={inactive ? "Reactivate" : "Mark inactive"}
                        data-testid={`toggle-active-${a.id}`}
                      >
                        <Power size={12} />
                      </button>
                    ) : (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full ${
                          inactive ? "bg-rose-100 text-rose-700" : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        {inactive ? "Inactive" : "Active"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-6 py-16 text-center text-stone-500 text-sm">
                {q ? `No accounts match "${q}"` : "No accounts yet."}
              </div>
            )}
          </div>
        </GlassCard>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((a) => {
            const inactive = a.active === false;
            const isEditing = editingId === a.id;
            return (
              <div
                key={a.id}
                onClick={() => { if (!isEditing) navigate(`/entries?account=${a.id}`); }}
                className={`glass rounded-2xl p-5 transition-transform relative ${isEditing ? "bg-amber-50/50 ring-2 ring-amber-200" : "hover:-translate-y-0.5 cursor-pointer"} ${inactive ? "opacity-50" : ""}`}
                data-testid={`accounts-card-${a.id}`}
              >
                <div className="absolute top-3 right-3 flex gap-1">
                  {a.source_upload_ids && a.source_upload_ids.length > 0 && !isEditing && (
                    <button
                      onClick={(e) => downloadUpload(e, a.source_upload_ids[0])}
                      className="p-1.5 rounded-full bg-white/70 text-stone-500 hover:text-[color:var(--gold-deep)] hover:bg-white transition-colors"
                      title="Download source file"
                      data-testid={`source-pdf-card-${a.id}`}
                    >
                      <FileText size={12} />
                    </button>
                  )}
                  {level === "owner" && !isEditing && (
                    <button
                      onClick={(e) => startRename(e, a)}
                      className="p-1.5 rounded-full bg-white/70 text-stone-500 hover:bg-white hover:text-stone-800 transition-colors"
                      title="Rename account"
                      data-testid={`account-rename-card-btn-${a.id}`}
                    >
                      <Pencil size={11} />
                    </button>
                  )}
                  {level === "owner" && !isEditing && (
                    <button
                      onClick={(e) => toggleActive(e, a)}
                      className={`p-1.5 rounded-full transition-colors ${inactive ? "bg-stone-100 text-stone-500 hover:bg-stone-200" : "bg-white/70 text-stone-500 hover:bg-white"}`}
                      title={inactive ? "Reactivate account" : "Mark inactive"}
                      data-testid={`toggle-active-card-${a.id}`}
                    >
                      <Power size={12} />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between pr-16">
                  {isEditing ? (
                    <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(e, a);
                          else if (e.key === "Escape") cancelRename(e);
                        }}
                        disabled={editSaving}
                        className="flex-1 px-2 py-1 rounded-lg bg-white border border-[color:var(--gold)]/50 text-sm outline-none focus:ring-2 focus:ring-[color:var(--gold)]/40"
                        data-testid={`account-rename-input-card-${a.id}`}
                      />
                      <button
                        onClick={(e) => commitRename(e, a)}
                        disabled={editSaving}
                        className="p-1 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                        title="Save"
                        data-testid={`account-rename-save-card-${a.id}`}
                      >
                        <Check size={12} />
                      </button>
                      <button
                        onClick={cancelRename}
                        disabled={editSaving}
                        className="p-1 rounded-full bg-stone-200 text-stone-700 hover:bg-stone-300 disabled:opacity-50"
                        title="Cancel"
                        data-testid={`account-rename-cancel-card-${a.id}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium truncate pr-2">{a.name}</div>
                      <div
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          inactive
                            ? "bg-stone-200 text-stone-600"
                            : a.balance > 0
                              ? "bg-emerald-50 text-emerald-700"
                              : a.balance < 0
                                ? "bg-rose-50 text-rose-700"
                                : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {inactive ? "Inactive" : a.balance > 0 ? "To collect" : a.balance < 0 ? "To pay" : "Zero"}
                      </div>
                    </>
                  )}
                </div>
                {isEditing && editError && (
                  <div className="text-[11px] text-rose-600 mt-1" data-testid={`account-rename-err-card-${a.id}`}>{editError}</div>
                )}
                <div
                  className={`font-serif text-3xl mt-3 ${
                    a.balance > 0
                      ? "text-emerald-700"
                      : a.balance < 0
                        ? "text-rose-700"
                        : "text-stone-600"
                  }`}
                >
                  {num(a.balance)}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  Opening {num(a.opening_balance)}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-stone-500 glass rounded-2xl">
              {q ? `No accounts match "${q}"` : "No accounts yet."}
            </div>
          )}
        </div>
      )}

      {open && (
        <CreateAccountDialog open={open} onClose={() => setOpen(false)} onSaved={refresh} />
      )}
    </div>
  );
}
